import { Shipment } from '../models/shipment';
import { PackagingTypeName } from './packaging-type-name';
import { WeightUnitEnum } from '../enum/weight-unit.enum';
import { DimensionUnitEnum } from '../enum/dimension-unit.enum';
import { SignatureOptionEnum } from '../enum/signature-option.enum';
import { InsuranceCategory } from '../models/insurance-category';
import { Currency } from './currency';

export class ShipmentSummary {

    static shipperSummary(s: Shipment): string {
        const parts: string[] = [];
        if (s.shipper.organization) {
            parts.push(s.shipper.organization);
        }
        if (s.shipper.postalCode) {
            parts.push(s.shipper.postalCode);
        } else if (s.shipper.city) {
            parts.push(s.shipper.city);
        }
        if (s.shipper.province) {
            parts.push(s.shipper.province);
        }
        parts.push(s.shipper.country);
        return parts.join(', ');
    }

    static destinationSummary(s: Shipment): string {
        const parts: string[] = [];
        if (s.destination.organization) {
            parts.push(s.destination.organization);
        }
        if (s.destination.postalCode) {
            parts.push(s.destination.postalCode);
        } else if (s.destination.city) {
            parts.push(s.destination.city);
        }
        if (s.destination.province) {
            parts.push(s.destination.province);
        }
        parts.push(s.destination.country);
        return parts.join(', ');
    }

    static soldToSummary(s: Shipment): string {
        const parts: string[] = [];
        if (s.soldTo.organization) {
            parts.push(s.soldTo.organization);
        }
        if (s.soldTo.postalCode) {
            parts.push(s.soldTo.postalCode);
        } else if (s.soldTo.city) {
            parts.push(s.soldTo.city);
        }
        if (s.soldTo.province) {
            parts.push(s.soldTo.province);
        }
        parts.push(s.soldTo.country);
        return parts.join(', ');
    }

    static brokerSummary(s: Shipment): string {
        const parts: string[] = [];
        if (s.broker.organization) {
            parts.push(s.broker.organization);
        }
        if (s.broker.postalCode) {
            parts.push(s.broker.postalCode);
        } else if (s.broker.city) {
            parts.push(s.broker.city);
        }
        if (s.broker.province) {
            parts.push(s.broker.province);
        }
        parts.push(s.broker.country);
        return parts.join(', ');
    }

    static totalActualWeight(s: Shipment): number {
        return s.packages.reduce((sum, pkg) => sum + +pkg.weight * +pkg.quantity, 0);
    }

    static totalPackages(s: Shipment): number {
        return s.packages.reduce((sum, pkg) => sum + (pkg.quantity || 1), 0);
    }

    static weightFormat(w: number, s: Shipment): string {
        if (s.weightUnit === WeightUnitEnum.KG) {
            return w.toFixed(2) + 'kg';
        }
        return w.toFixed(1) + 'lbs';
    }

    static dimensionFormat(d: number, s: Shipment): string {
        if (s.dimensionUnit === DimensionUnitEnum.CM) {
            return d.toFixed(0) + 'cm';
        }
        return d.toFixed(1) + '"';
    }

    static packageSummary(s: Shipment): string {
        const totalPackages = ShipmentSummary.totalPackages(s);
        const totalWeight = ShipmentSummary.weightFormat(ShipmentSummary.totalActualWeight(s), s);
        const packagingType = PackagingTypeName.describe(s.packagingType);
        return `${totalPackages} x ${packagingType} (${totalWeight})`;
    }

    static optionSummary(s: Shipment): string {
        const parts: string[] = [ s.shipmentDate ];
        if (s.signatureOption === SignatureOptionEnum.NOT_REQUIRED) {
            parts.push('No Signature Required');
        }
        if (s.signatureOption === SignatureOptionEnum.REQUIRED) {
            parts.push('Signature Required');
        }
        return parts.join(', ');
    }

    static internationalSummary(s: Shipment): string {
        const parts: string[] = [ s.courierForms.termsofsale ];
        return parts.join(', ');
    }

    static referencesSummary(s: Shipment): string {
        return Object.values(s.references).join(', ');
    }

    static insuranceSummary(s: Shipment, c: InsuranceCategory): string {
        const total = s.packages.reduce((t, p) => t + p.value, 0);
        return Currency.render(total, s.currency) + (c ? ' ' + c.caption : '');
    }
}
